// import api from "@/lib/api/axios"; // Commented out for now

// Types
export interface Product {
  id: number;
  name: string;
  price: string;
  originalPrice?: string;
  discount?: string;
  location: string;
  distance: string;
  time: string;
  image: string;
  seller_id?: string;
  description?: string;
  category?: string;
  isPromoted?: boolean;
  promotionText?: string;
}

export interface ProductListResponse {
  results: Product[];
  count: number;
  next: string | null;
  previous: string | null;
}

export interface ProductListParams {
  page?: number;
  search?: string;
  category?: string;
  ordering?: string;
  seller_id?: string;
  timeFilter?: string;
  priceSort?: string;
  userProducts?: boolean;
}

export interface Seller {
  id: string;
  name: string;
  avatar: string;
  joinDate: string;
}

export interface CartItem {
  id: string;
  product: Product;
  quantity: number;
}

// Mock data for marketplace products
const mockProducts: Product[] = [
  {
    id: 1,
    name: "iPhone 17 Promax",
    price: "15000000",
    discount: "20%",
    location: "Hà Nội",
    distance: "5.5km",
    time: "3 giờ trước",
    image: "/images/iphone.png",
    category: "Đồ điện tử",
    isPromoted: false
  },
  {
    id: 2,
    name: "Robot hút bụi",
    price: "5600000",
    originalPrice: "7000000",
    discount: "20%",
    location: "Hà Nội",
    distance: "3.2km",
    time: "5 giờ trước",
    image: "/images/robot.png",
    category: "Đồ gia dụng",
    isPromoted: false,
    promotionText: "LÀU NHÀ HIỆU QUẢ"
  },
  {
    id: 3,
    name: "Máy sấy",
    price: "14500000",
    originalPrice: "16000000",
    discount: "40%",
    location: "Hà Nội",
    distance: "5.2km",
    time: "2 giờ trước",
    image: "/images/maysay.png",
    category: "Đồ gia dụng",
    isPromoted: false,
    description: "Đính hình vi chất nước, ăn tóc con chỉ trong 1 lần sấy. Máng lại mái tóc mềm mượt, óng ả. Được thiết kế chắn sóc tóc và da đầu. Nhưng vẫn khô và không gây hư tổn tóc từ nhiệt. Bảo hành 2 năm. Đổi hàng trong 14 ngày."
  },
  {
    id: 4,
    name: "Tủ lạnh",
    price: "15000000",
    discount: "30%",
    location: "Hà Nội",
    distance: "7.1km",
    time: "1 ngày trước",
    image: "/images/tulanh.png",
    category: "Đồ gia dụng",
    isPromoted: false
  }
];

// Mock data for sellers
const mockSellers: Seller[] = [
  {
    id: "1",
    name: "Kiki",
    avatar: "/avatars/kiki.svg",
    joinDate: "Tháng 5, 2023"
  },
  {
    id: "2",
    name: "Minh Anh",
    avatar: "/avatars/haku.svg",
    joinDate: "Tháng 3, 2022"
  }
];

// API functions
export const marketplaceApi = {
  /**
   * Get list of products with pagination, filtering, and search
   */
  getProducts: async (params?: ProductListParams): Promise<ProductListResponse> => {
    // In a real app, this would be an API call
    // return api.get<ProductListResponse>("/api/marketplace/products/", { params });

    // For now, we'll use mock data
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay

    let filteredProducts = [...mockProducts];

    // Apply search filter
    if (params?.search) {
      const searchLower = params.search.toLowerCase();
      filteredProducts = filteredProducts.filter(product =>
        product.name.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower) ||
        product.category?.toLowerCase().includes(searchLower)
      );
    }

    // Apply category filter
    if (params?.category) {
      filteredProducts = filteredProducts.filter(product =>
        product.category === params.category
      );
    }

    // Apply seller filter
    if (params?.seller_id) {
      filteredProducts = filteredProducts.filter(product =>
        product.seller_id === params.seller_id
      );
    }

    // Apply ordering
    if (params?.ordering) {
      // This is a simplified example - in a real app, you'd handle more complex ordering
      if (params.ordering === "price") {
        filteredProducts.sort((a, b) =>
          parseFloat(a.price.replace(/[^\d]/g, "")) - parseFloat(b.price.replace(/[^\d]/g, ""))
        );
      } else if (params.ordering === "-price") {
        filteredProducts.sort((a, b) =>
          parseFloat(b.price.replace(/[^\d]/g, "")) - parseFloat(a.price.replace(/[^\d]/g, ""))
        );
      }
    }

    // Apply pagination
    const page = params?.page || 1;
    const pageSize = 10;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

    return {
      results: paginatedProducts,
      count: filteredProducts.length,
      next: endIndex < filteredProducts.length ? `?page=${page + 1}` : null,
      previous: page > 1 ? `?page=${page - 1}` : null
    };
  },

  /**
   * Get a single product by ID
   */
  getProduct: async (id: number): Promise<Product> => {
    // In a real app, this would be an API call
    // return api.get<Product>(`/api/marketplace/products/${id}/`);

    // For now, we'll use mock data
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate API delay

    const product = mockProducts.find(p => p.id === id);
    if (!product) {
      throw new Error("Product not found");
    }

    return product;
  },

  /**
   * Get user's products
   */
  getUserProducts: async (): Promise<ProductListResponse> => {
    // In a real app, this would be an API call
    // return api.get<ProductListResponse>(`/api/marketplace/users/me/products/`);

    // For now, we'll return an empty list
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate API delay

    return {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
  },

  /**
   * Get related products
   */
  getRelatedProducts: async (productId: number, limit: number = 4): Promise<Product[]> => {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate API delay

    const product = mockProducts.find(p => p.id === productId);
    if (!product) {
      return [];
    }

    // Filter products by same category but exclude current product
    const related = mockProducts
      .filter(p => p.category === product.category && p.id !== productId)
      .slice(0, limit);

    return related;
  },

  /**
   * Get seller by ID
   */
  getSeller: async (sellerId: string): Promise<Seller | undefined> => {
    await new Promise(resolve => setTimeout(resolve, 200)); // Simulate API delay
    return mockSellers.find(s => s.id === sellerId);
  },

  /**
   * Get seller's products
   */
  getSellerProducts: async (sellerId: string): Promise<ProductListResponse> => {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate API delay

    const sellerProducts = mockProducts.filter(p => p.seller_id === sellerId);

    return {
      results: sellerProducts,
      count: sellerProducts.length,
      next: null,
      previous: null
    };
  },

  /**
   * Get trending products
   */
  getTrendingProducts: async (limit: number = 4): Promise<Product[]> => {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate API delay

    // In a real app, this would be based on views, sales, etc.
    // For now, we'll just return random products
    const shuffled = [...mockProducts].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, limit);
  },

  /**
   * Get promoted products
   */
  getPromotedProducts: async (limit: number = 4): Promise<Product[]> => {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate API delay

    const promoted = mockProducts.filter(p => p.isPromoted);
    return promoted.slice(0, limit);
  },

  /**
   * Get product categories
   */
  getCategories: async (): Promise<string[]> => {
    await new Promise(resolve => setTimeout(resolve, 200)); // Simulate API delay

    // Get unique categories from mock products
    const categories = Array.from(new Set(mockProducts.map(p => p.category).filter(Boolean)));
    return categories as string[];
  }
}; 